import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Seo from "../components/Seo";
import PlayerCard from "../components/PlayerCard";
import NotFound from "./NotFound";
import { fetchTeam } from "../cms/teams";
import { fetchSpieler } from "../cms/spieler";

export default function TeamDetail() {
  const { id } = useParams();
  const [team, setTeam]       = useState(null);
  const [spieler, setSpieler] = useState([]);
  const [ladend, setLadend]   = useState(true);

  useEffect(() => {
    let aktiv = true;
    setLadend(true);
    fetchTeam(id).then((t) => {
      if (!aktiv) return;
      setTeam(t);
      if (t) fetchSpieler(id).then((s) => aktiv && setSpieler(s));
    }).finally(() => aktiv && setLadend(false));
    return () => { aktiv = false; };
  }, [id]);

  if (ladend) {
    return (
      <section className="mx-auto max-w-4xl px-4 py-16">
        <div className="bg-dark/5 animate-pulse h-96" />
      </section>
    );
  }

  if (!team) return <NotFound />;

  const bildUrl = team.bild || null;
  const akzent  = team.akzentfarbe || "#003568";
  const trainerListe = Array.isArray(team.trainer)
    ? team.trainer
    : team.trainer
    ? [{ name: team.trainer }]
    : [];

  return (
    <>
      <Seo
        title={team.name}
        description={team.beschreibung || `${team.name} — Kader, Trainer und Saison-Infos.`}
        path={`/teams/${team.id}`}
      />

      {/* Zurück-Link */}
      <div className="mx-auto max-w-6xl px-4 pt-8">
        <Link to="/teams" className="inline-flex items-center gap-2 text-sm font-semibold text-dark/60 hover:text-primary transition-colors">
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Alle Teams
        </Link>
      </div>

      {/* Headerbild */}
      {bildUrl && (
        <div className="mx-auto max-w-6xl px-4 mt-6">
          <div className="h-64 md:h-96 overflow-hidden bg-dark/5">
            <img src={bildUrl} alt={team.name} loading="lazy" decoding="async" className="h-full w-full object-cover" />
          </div>
        </div>
      )}

      <div className="mx-auto max-w-6xl px-4">
        <div className="h-1.5 mt-6" style={{ backgroundColor: akzent }} />
      </div>

      {/* Team-Infos */}
      <section className="mx-auto max-w-6xl px-4 py-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-4xl md:text-5xl font-extrabold uppercase">{team.name}</h1>
            <p className="text-sm text-dark/60 mt-2">
              {team.jahrgang}{team.jahrgang && team.liga ? " — " : ""}{team.liga}
            </p>
          </div>
          {team.spielplanUrl && (
            <a
              href={team.spielplanUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-shrink-0 inline-flex items-center gap-2 bg-primary text-white text-xs font-bold uppercase tracking-wide px-5 py-2.5 hover:bg-accent transition-colors"
            >
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Spielplan
            </a>
          )}
        </div>

        {team.beschreibung && (
          <p className="mt-5 text-dark/75 leading-relaxed max-w-3xl">{team.beschreibung}</p>
        )}

        {/* Staff */}
        {trainerListe.length > 0 && (
          <div className="mt-8">
            <p className="text-xs font-bold uppercase tracking-wide text-dark/40 mb-3">Staff</p>
            <ul className="space-y-2">
              {trainerListe.map((t) => (
                <li key={t.name} className="flex items-center gap-2 text-sm">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent flex-shrink-0" />
                  <span className="font-medium">{t.name}</span>
                  {t.rolle && <span className="text-dark/50">— {t.rolle}</span>}
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {/* Kader / Spieler-Portraits */}
      <section className="bg-light">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <p className="text-accent font-bold uppercase tracking-widest text-xs mb-3">Kader</p>
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase mb-8">Spieler</h2>

          {spieler.length === 0 ? (
            <p className="text-dark/60">Noch keine Spieler erfasst.</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {spieler.map((s) => (
                <PlayerCard key={s.id} spieler={s} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
